import {useCallback, useEffect, useId, useMemo, useRef, useState} from "react";

/**
 * Accessible combobox for free-text input with a filtered suggestion
 * dropdown. Replaces the previous HTML `<datalist>` usage on the upload
 * form, which was not styleable across browsers and rendered as a
 * native list (Khubaib bug #18).
 *
 * Behaviour:
 *  - User can type any value (free entry — categories are user-defined).
 *  - As they type, the dropdown filters the supplied `options` by
 *    case-insensitive substring match.
 *  - Up/Down arrows move highlight; Enter selects the highlighted
 *    option (or commits the typed value if none highlighted); Escape
 *    closes the dropdown without changing the value.
 *  - Mouse: click on an option selects it; clicking outside closes the
 *    dropdown.
 *
 * Props mirror `UploadFormTextInput` so swap-in is straightforward.
 */
const CategoryCombobox = ({
  className,
  title,
  placeholder,
  value,
  onChange,
  disabled,
  iconClass: Icon,
  options = [],
}) => {
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef(null);
  const listboxRef = useRef(null);
  const containerRef = useRef(null);
  const listboxId = useId();

  const trimmedValue = (value || "").trim();
  const lowerValue = trimmedValue.toLowerCase();

  const filteredOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    if (!lowerValue) return options;
    return options.filter((opt) =>
      (opt || "").toLowerCase().includes(lowerValue),
    );
  }, [options, lowerValue]);

  // Reset highlight when filter set changes.
  useEffect(() => {
    setHighlightIndex(filteredOptions.length > 0 ? 0 : -1);
  }, [filteredOptions]);

  // Close on outside click. Uses mousedown so a click on an option
  // (handled by onMouseDown below) still registers before the
  // controller closes.
  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (event) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("touchstart", handlePointer);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("touchstart", handlePointer);
    };
  }, [open]);

  const selectOption = useCallback(
    (option) => {
      onChange({target: {value: option}});
      setOpen(false);
      // Move focus back to the input so subsequent keystrokes keep
      // editing rather than blurring.
      inputRef.current?.focus();
    },
    [onChange],
  );

  const handleKeyDown = (event) => {
    if (disabled) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlightIndex((idx) =>
        filteredOptions.length === 0 ? -1 : (idx + 1) % filteredOptions.length,
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlightIndex((idx) =>
        filteredOptions.length === 0
          ? -1
          : (idx - 1 + filteredOptions.length) % filteredOptions.length,
      );
    } else if (event.key === "Enter") {
      if (
        open &&
        highlightIndex >= 0 &&
        highlightIndex < filteredOptions.length
      ) {
        event.preventDefault();
        selectOption(filteredOptions[highlightIndex]);
      } else {
        // Let the surrounding form decide what to do with Enter on a
        // free-typed value -- we just close the dropdown.
        setOpen(false);
      }
    } else if (event.key === "Escape") {
      if (open) {
        event.preventDefault();
        setOpen(false);
      }
    } else if (event.key === "Tab") {
      // Don't trap focus -- just close so the next field is clean.
      setOpen(false);
    }
  };

  const showDropdown = open && !disabled && filteredOptions.length > 0;

  return (
    <div className={className} ref={containerRef}>
      <label className="block text-sm font-semibold text-slate-800 ml-1">
        {title}
      </label>
      <div className="relative">
        {Icon ? (
          <Icon
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            size={18}
          />
        ) : null}
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            showDropdown && highlightIndex >= 0
              ? `${listboxId}-opt-${highlightIndex}`
              : undefined
          }
          autoComplete="off"
          value={value || ""}
          onChange={(event) => {
            onChange(event);
            if (!open) setOpen(true);
          }}
          onFocus={() => {
            if (!disabled) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-3xl outline-none text-slate-900 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition"
        />
        {showDropdown && (
          <ul
            ref={listboxRef}
            id={listboxId}
            role="listbox"
            className="absolute z-20 mt-1 left-0 right-0 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-2xl shadow-lg py-1"
          >
            {filteredOptions.map((option, index) => {
              const isHighlighted = index === highlightIndex;
              return (
                <li
                  key={option}
                  id={`${listboxId}-opt-${index}`}
                  role="option"
                  aria-selected={isHighlighted}
                  onMouseDown={(event) => {
                    // mousedown (not click) so we win the race against
                    // the outside-click handler that fires on the same
                    // pointer event.
                    event.preventDefault();
                    selectOption(option);
                  }}
                  onMouseEnter={() => setHighlightIndex(index)}
                  className={`px-4 py-2 text-sm cursor-pointer select-none ${
                    isHighlighted
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {option}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CategoryCombobox;
